import { useEffect, useState } from "react";
import AdvancedCarousel from "../components/AdvancedCarousel";
import Brand from "../components/Brand";
import Item from "../components/Item";
import Navbar from "../components/Navbar";
import brands from "../dummyData/brands";
import categories from "../dummyData/categories";
import items from "../dummyData/items";
import ItemModal from "../components/ItemModal";
import { useCompanies } from "../hooks/useCompanies";
import type { CompanyType } from "../types/company";
import { useCategories } from "../hooks/useCategories";
import type { CategoryType } from "../types/category";
import type { ItemType } from "../types/item";
import Category from "../components/Category";
import { usePopularItems } from "../hooks/usePopularItems";
import { useCategoryItems } from "../hooks/useCategoryItems";
import { useLocation, useNavigate, useParams } from "react-router";
import { Loader2 } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import DownloadAppBanner from "@/assets/DownloadAppBanner";
import { useItemModal } from "@/contexts/ItemModalProvider";
import { useTranslation } from "react-i18next";
import { useNewItems } from "@/hooks/useNewItems";
import toast, { Toaster } from "react-hot-toast";
import { useMainCategoryCompanies } from "@/hooks/useMainCategoryCompanies";
import { useMainCategories } from "@/hooks/useMainCategories";

type Item = {
  name: string;
  price: number;
  description: string;
  itemImageUrl: string;
  sourceImageUrl?: string;
  sourceName?: string;
  sourceDescription?: string;
};

export default function homeScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const categoryId = params.categoryId;
  const mainCategoryId = params.mainCategoryId;

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    categoryId || null
  );
  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState<
    string | null
  >(mainCategoryId || null);

  const { data: mainCategories, isLoading: isMainCategoriesLoading } =
    useMainCategories() || [];

  const {
    data: mainCategoryCompanies,
    isLoading: isMainCategoryCompaniesLoading,
  } = useMainCategoryCompanies(selectedMainCategoryId || "") || [];

  console.log("mainCategoryCompanies", mainCategoryCompanies);
  console.log("isMainCategoryCompaniesLoading", isMainCategoryCompaniesLoading);

  const { t } = useTranslation();

  const location = useLocation();

  useEffect(() => {
    const flash = location.state?.flash;
    window.history.replaceState({}, "");
    if (flash) {
      toast.success(flash, {
        position: "bottom-center",
      });
    }
  }, []);

  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [itemInModal, setItemInModal] = useState<Item | null>(null);

  const { data: companies, isLoading: isCompaniesLoading } = useCompanies();
  const { data: categories, isLoading: isCategoriesLoading } = useCategories();
  const { data: popularItems, isLoading: isPopularItemsLoading } =
    usePopularItems({});

  const { data: newItems, isLoading: isNewItemsLoading } = useNewItems({});

  const { data: categoryItems, isLoading: isCategoryItemsLoading } =
    useCategoryItems(selectedCategoryId || "");

  const { open } = useItemModal();

  const isDesktop = window.innerWidth > 768;

  useEffect(() => {
    if (location.pathname === "/") {
      setSelectedCategoryId(null);
      setSelectedMainCategoryId(null);
    }
    if (location.pathname === `/categories/${categoryId}`) {
      setSelectedCategoryId(categoryId || null);
    }
    if (location.pathname === `/main-categories/${mainCategoryId}`) {
      setSelectedMainCategoryId(mainCategoryId || null);
    }
  }, [location.pathname]);

  return (
    <div>
      <div
        className={`px-4 sm:px-6 md:px-8 lg:px-16 xl:px-24 flex flex-col gap-8 py-8 md:gap-16 md:py-16`}
      >
        {!selectedCategoryId && !selectedMainCategoryId && (
          <AdvancedCarousel
            title={t("brands")}
            data={companies || []}
            loading={isCompaniesLoading}
            render={(company: CompanyType) => (
              <Brand
                name={company.name}
                imageUrl={company.image}
                onClick={() => {
                  navigate(`/restaurants/${company.id}`);
                }}
              />
            )}
            onViewAllClick={() => {
              navigate("/restaurants");
            }}
          />
        )}

        {!selectedCategoryId && (
          <AdvancedCarousel
            title={t("main_categories")}
            data={mainCategories || []}
            loading={isCategoriesLoading}
            render={(item: CategoryType) => (
              <Category
                name={item.name}
                placesCount={10}
                imageUrl={item.image}
                mini={selectedMainCategoryId ? true : false}
                onClick={() => {
                  setSelectedMainCategoryId(item.id);
                  navigate(`/main-categories/${item.id}`);
                }}
                highlighted={selectedMainCategoryId === item.id}
              />
            )}
          />
        )}

        {!selectedMainCategoryId && (
          <AdvancedCarousel
            title={t("categories")}
            data={categories || []}
            loading={isCategoriesLoading}
            render={(item: CategoryType) => (
              <Category
                name={item.name}
                placesCount={10}
                imageUrl={item.image}
                mini={selectedCategoryId ? true : false}
                onClick={() => {
                  // setSelectedCategoryId(item.id);
                  navigate(`/categories/${item.id}`);
                }}
                highlighted={selectedCategoryId === item.id}
              />
            )}
          />
        )}
        {!selectedCategoryId && !selectedMainCategoryId && isDesktop && (
          <DownloadAppBanner />
        )}

        {selectedMainCategoryId &&
          (mainCategoryCompanies?.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {mainCategoryCompanies?.map((company: any) => (
                <Brand
                  key={company.id}
                  name={company.name}
                  imageUrl={company.image}
                  onClick={() => {
                    navigate(`/restaurants/${company.id}`);
                  }}
                />
              ))}
            </div>
          ) : isMainCategoryCompaniesLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="rounded-xl"
                  height={256}
                  duration={0.5}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center gap-4 py-12">
              <div className="w-32 h-32 sm:w-40 sm:h-40 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-2">
                <h1 className="text-xl sm:text-2xl font-bold tracking-wide">
                  {t("no_items_found")}
                </h1>
                <p className="text-sm text-gray-500 px-4">
                  {t("no_items_description")}
                </p>
              </div>
            </div>
          ))}
        {selectedCategoryId && (
          <>
            {isCategoryItemsLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className="rounded-xl"
                    height={256}
                    duration={0.5}
                  />
                ))}
              </div>
            ) : categoryItems && categoryItems?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {categoryItems.map((item) => (
                  <Item
                    key={item.id}
                    imageUrl={item.image || undefined}
                    name={item.name}
                    restaurantName={item.companyName}
                    price={item.price}
                    onClick={() => {
                      open({
                        item,
                        ctaText: "Order",
                        onCtaClick: (ingredients, quantity) => {
                          navigate(
                            `/restaurants/${item.companyId}/items/${item.id}`
                          );
                        },
                      });
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center gap-4 py-12">
                <div className="w-32 h-32 sm:w-40 sm:h-40 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
                <div className="flex flex-col gap-2">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-wide">
                    {t("no_items_found")}
                  </h1>
                  <p className="text-sm text-gray-500 px-4">
                    {t("no_items_description")}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {!selectedCategoryId && !selectedMainCategoryId && (
          <>
            <AdvancedCarousel
              title={t("top_picks")}
              data={popularItems || []}
              loading={isPopularItemsLoading}
              itemWidth={256}
              onViewAllClick={() => {
                navigate(`/popular-items`);
              }}
              render={(item: ItemType) => (
                <Item
                  name={item.name}
                  imageUrl={item.image ?? ""}
                  restaurantName={item.companyName}
                  price={item.price}
                  onClick={() => {
                    open({
                      item,
                      ctaText: "Order",
                      onCtaClick: (ingredients, quantity) => {
                        navigate(
                          `/restaurants/${item.companyId}/items/${item.id}`
                        );
                      },
                    });
                  }}
                />
              )}
            />

            <AdvancedCarousel
              title={t("new_items")}
              data={newItems || []}
              loading={isNewItemsLoading}
              onViewAllClick={() => {
                navigate(`/new-items`);
              }}
              itemWidth={256}
              render={(item: ItemType) => (
                <Item
                  name={item.name}
                  imageUrl={item.image || undefined}
                  restaurantName={item.companyName}
                  price={item.price}
                  onClick={() => {
                    open({
                      item,
                      ctaText: "Order",
                      onCtaClick: (ingredients, quantity) => {
                        navigate(
                          `/restaurants/${item.companyId}/items/${item.id}`
                        );
                      },
                    });
                  }}
                />
              )}
            />

            {/* <AdvancedCarousel
              title="Deals"
              data={items}
              render={(item) => (
                <Item
                  name={item.name}
                  imageUrl={item.image}
                  restaurantName={item.restaurantName}
                  price={item.price}
                  priceAfterDiscount={item.priceAfterDiscount}
                />
              )}
            />

            <AdvancedCarousel
              title="7-Eleven"
              imageUrl="https://tb-static.uber.com/prod/image-proc/processed_images/843a5816cbdd0e5df8525788a4657cd6/e00617ce8176680d1c4c1a6fb65963e2.png"
              data={items}
              render={(item) => (
                <Item
                  name={item.name}
                  imageUrl={item.image}
                  restaurantName={item.restaurantName}
                  price={item.price}
                  priceAfterDiscount={item.priceAfterDiscount}
                  onClick={() => {
                    
                    setIsItemModalOpen(true);
                  }}
                />
              )}
            /> */}
          </>
        )}
      </div>

      {/* <ItemModal
        open={isItemModalOpen}
        onClose={() => {
          setIsItemModalOpen(false);
        }}
        onSourceClick={() => {
          setIsItemModalOpen(false);
        }}
        onOrder={() => {
          setIsItemModalOpen(false);
        }}
        name={itemInModal?.name || ""}
        price={itemInModal?.price || 0}
        description={itemInModal?.description || ""}
        itemImageUrl={itemInModal?.itemImageUrl || ""}
        sourceImageUrl={itemInModal?.sourceImageUrl || ""}
        sourceName={itemInModal?.sourceName || ""}
        sourceDescription={itemInModal?.sourceDescription || ""}
      /> */}
    </div>
  );
}
